import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { ScrollCarousel } from 'dibs-scroll-carousel/exports/ScrollCarousel.ts';
import { HpSharedCollectionsModuleItem } from './HpSharedCollectionsModuleItem.tsx';
import { HpSharedModuleContainer } from '../HpSharedModuleContainer/HpSharedModuleContainer.tsx';
import { HpSharedCarouselTracking } from '../HpSharedCarouselTracking/HpSharedCarouselTracking.tsx';
import {
    trackCollectionsImpression,
    trackCollectionsItemClick,
    trackArrowClick,
} from './helpers/CollectionsTracking.js';
import { trackModuleLocation, trackModule } from '../helpers/moduleLocationTracking.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';

import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './HpSharedCollectionsModule.scss';

import {
    type HpSharedCollectionsModule_componentModule$key,
    type HpSharedCollectionsModule_componentModule$data,
} from './__generated__/HpSharedCollectionsModule_componentModule.graphql.ts';
import { useHomePageContext } from '../HomePageContext.tsx';

const componentModuleFragment = graphql`
    fragment HpSharedCollectionsModule_componentModule on CollectionsModule {
        title
        cmsDisplayTitle
        items {
            cmsId
            title # used in tracking
            ...HpSharedCollectionsModuleItem_item
        }
    }
`;

type CollectionItems = NonNullable<HpSharedCollectionsModule_componentModule$data['items']>;

type Props = {
    componentModule: HpSharedCollectionsModule_componentModule$key;
    useLazyLoadImages: boolean;
    moduleIndex: number;
    totalModules: number;
};

export const HpSharedCollectionsModule: FC<Props> = ({
    componentModule: componentModuleRef,
    useLazyLoadImages,
    moduleIndex,
    totalModules,
}) => {
    const componentModule = useFragment(componentModuleFragment, componentModuleRef);
    const { isMobile } = useHomePageContext();

    if (!componentModule) {
        return null;
    }

    const { title, cmsDisplayTitle } = componentModule;
    const items = (componentModule.items || []).filter(filterNulls);
    const itemsPerPage = isMobile ? 1 : 5;

    return (
        <HpSharedModuleContainer
            className={styles.wrapper}
            dataTn="collections-container"
            addTopGap={!title}
        >
            <div
                className={classnames(
                    styles.subHeader,
                    dibsCss.sassyFontHeaderSizeLarge,
                    dibsCss.m0,
                    dibsCss.pyLarge,
                    dibsCss.px0
                )}
            >
                {title}
            </div>

            <HpSharedCarouselTracking
                items={items}
                itemsPerPage={itemsPerPage}
                onItemsImpression={({
                    visibleItems,
                    index,
                }: {
                    visibleItems: CollectionItems;
                    index: number;
                }) =>
                    trackCollectionsImpression({
                        items: visibleItems,
                        startIndex: index,
                        name: title,
                    })
                }
            >
                {({ handlePageChange }) => (
                    <ScrollCarousel
                        gapSize={isMobile ? 'small' : 'large'}
                        itemsToShow={isMobile ? 1.5 : 5}
                        scrollPadding={isMobile ? 'small' : 'none'}
                        showArrows={!isMobile && items.length > itemsPerPage}
                        isInfinite={!isMobile}
                        enableScroll={isMobile}
                        stepSize={itemsPerPage}
                        onArrowClick={direction => {
                            trackArrowClick({ type: direction });
                        }}
                        onSlideChange={({ activeSlide }) => {
                            // activeSlide is 1-based, so we need to subtract 1 for correct page calculation
                            const page = Math.ceil((activeSlide - 1) / itemsPerPage);
                            handlePageChange(page);
                        }}
                    >
                        {items.map((item, index) => (
                            <HpSharedCollectionsModuleItem
                                key={item.cmsId}
                                item={item}
                                useLazyLoadImages={useLazyLoadImages}
                                onClick={() => {
                                    trackCollectionsItemClick({
                                        item: item,
                                        index,
                                    });
                                    trackModuleLocation({
                                        label: 'small item carousel module',
                                        moduleIndex,
                                        totalModules,
                                    });
                                    trackModule(cmsDisplayTitle);
                                }}
                            />
                        ))}
                    </ScrollCarousel>
                )}
            </HpSharedCarouselTracking>
        </HpSharedModuleContainer>
    );
};
